import React, { useState, useEffect, useRef, useLayoutEffect, useCallback, createContext, useContext } from 'react';
import { User, Users, TrendingUp, Crown, RefreshCw, AlertTriangle, ChevronLeft } from 'lucide-react';
import API_BASE_URL from '../config/api';

// Create a context to track node positions globally within the tree
const TreeContext = createContext(null);

// Helper to map API response to binary tree format
const mapResponseToTree = (node) => {
    if (!node) return null;
    const mapped = {
        id: node.id,
        db_id: node.id,
        name: node.name,
        user_id: node.user_id || node.user_identity_id || '',
        referral_code: node.referral_code || '',
        pv: node.user_pv_points || node.pv_points || node.pv || 0,
        position: node.position,
        is_root: node.is_root || false,
        package: node.package || node.package_name || 'Starter',
        wallet_balance: node.wallet_balance || 0,
        left_count: node.left_count || 0,
        right_count: node.right_count || 0,
        left_pv: node.left_pv || 0,
        right_pv: node.right_pv || 0,
        left: null,
        right: null
    };

    if (node.children && Array.isArray(node.children) && node.children.length > 0) {
        const getPos = (c) => String(c?.position ?? '').trim().toLowerCase();
        const leftNodeByPos = node.children.find(c => getPos(c) === 'l' || getPos(c) === 'left');
        const rightNodeByPos = node.children.find(c => getPos(c) === 'r' || getPos(c) === 'right');
        const leftNode = leftNodeByPos ?? node.children?.[0] ?? null;
        const rightNode = rightNodeByPos ?? (node.children?.length > 1 ? node.children?.[1] : null) ?? null;

        mapped.left = mapResponseToTree(leftNode);
        mapped.right = (rightNode && rightNode !== leftNode) ? mapResponseToTree(rightNode) : null;
    } else if (node.left || node.right) {
        mapped.left = mapResponseToTree(node.left);
        mapped.right = mapResponseToTree(node.right);
    }
    return mapped;
};

// Get initials from name
const getInitials = (name) => {
    if (!name) return '??';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
};

// ==================== USER CARD ====================
const UserCard = ({ user, isRoot = false, position, side, cardRef, onUserClick }) => {
    if (!user) return null;

    const sideLower = String(side || position).toLowerCase();
    const isLeft = sideLower === 'left' || sideLower === 'l';
    const isRight = sideLower === 'right' || sideLower === 'r';

    const isInactive = !user.package || user.package === 'None' || user.package === 'STARTER' || Number(user.pv || 0) <= 0;


    const handleClick = () => {
        if (onUserClick && !isRoot) {
            onUserClick(user);
        }
    };

    return (
        <div ref={cardRef}
            onClick={handleClick}
            className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 relative cursor-pointer"
            style={{ width: '210px' }}
        >
            {/* Premium Package Header */}
            <div className={`px-4 py-2.5 flex items-center justify-between ${isInactive ? 'bg-red-600' : isRoot ? 'bg-amber-500' : isLeft ? 'bg-blue-600' : isRight ? 'bg-purple-600' : 'bg-gray-600'}`}>
                <span className="text-[9px] font-black uppercase tracking-[0.15em] text-white/95 truncate pr-2">{user.package || 'STARTER'}</span>
                <div className="flex items-center">
                    <div className={`w-1.5 h-1.5 rounded-full bg-white shadow-[0_0_10px_rgba(255,255,255,1)] ${isInactive ? 'bg-white/40' : 'animate-pulse'}`}></div>
                </div>
            </div>

            {/* User Info */}
            <div className="px-4 py-4 flex items-center gap-3">
                <div className={`w-11 h-11 rounded-full flex items-center justify-center text-white text-base font-black shrink-0 shadow-lg ${isInactive ? 'bg-gradient-to-br from-red-500 to-red-600' : isRoot ? 'bg-gradient-to-br from-amber-400 to-amber-500' : isLeft ? 'bg-gradient-to-br from-blue-500 to-blue-600' : isRight ? 'bg-gradient-to-br from-purple-500 to-purple-600' : 'bg-gradient-to-br from-gray-400 to-gray-500'}`}>
                    {getInitials(user.name)}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-black text-gray-900 truncate leading-tight">{user.name || 'Unknown'}</p>
                    <p className="text-[10px] font-bold text-gray-400/80 truncate mt-0.5">{user.referral_code || user.user_id || '---'}</p>
                </div>
                {isRoot && <Crown size={16} className="text-amber-500 shrink-0 filter drop-shadow-sm" />}
            </div>

            {/* Subtle Separator */}
            <div className="mx-4 border-t border-gray-50"></div>

            {/* PV Row */}
            <div className="px-4 py-3 flex justify-between items-center bg-gray-50/30">
                <div className="flex items-center gap-1.5">
                    <span className="text-[8px] text-gray-400 font-black uppercase tracking-wider">PV:</span>
                    <span className={`text-[13px] font-black ${isInactive ? 'text-red-600' : isRoot ? 'text-amber-600' : isLeft ? 'text-blue-600' : isRight ? 'text-purple-600' : 'text-emerald-600'}`}>
                        {Number(user.pv || 0).toLocaleString()}
                    </span>
                </div>
                <div className="flex items-center gap-2 text-[9px] font-bold text-gray-400">
                    <span className="bg-gray-100/80 px-1.5 py-0.5 rounded">L: {user.left_count || 0}</span>
                    <span className="bg-gray-100/80 px-1.5 py-0.5 rounded">R: {user.right_count || 0}</span>
                </div>
            </div>

            {/* Drill-down hint */}
            {!isRoot && (user.left_count > 0 || user.right_count > 0) && (
                <div className="px-3 pb-2 flex justify-end">
                    <div className="flex items-center gap-0.5 text-[8px] font-extrabold text-blue-500 uppercase tracking-tight">
                        <span>View Team</span>
                        <ChevronLeft size={8} className="rotate-180" />
                    </div>
                </div>
            )}
        </div>
    );
};

// ==================== EMPTY SLOT ====================
const EmptySlot = ({ position, node_id, side }) => {
    const { registerNode, unregisterNode } = useContext(TreeContext);
    const sideLower = String(side || position).toLowerCase();
    const isLeft = sideLower === 'left' || sideLower === 'l';
    const slotId = `empty-${node_id}-${position}`;
    const myRef = useRef(null);

    useLayoutEffect(() => {
        if (myRef.current) {
            registerNode(slotId, myRef, position, 999, true);
        }
        return () => unregisterNode(slotId);
    }, [slotId, position, registerNode, unregisterNode]);

    return (
        <div ref={myRef}
            className={`rounded-xl border-2 border-dashed flex flex-col items-center justify-center transition-all
                ${isLeft ? 'border-blue-200 bg-blue-50/40' : 'border-purple-200 bg-purple-50/40'}`}
            style={{ width: '200px', height: '80px' }}
        >
            <div className={`w-8 h-8 rounded-full border-2 border-dashed flex items-center justify-center mb-1
                ${isLeft ? 'border-blue-300 text-blue-300' : 'border-purple-300 text-purple-300'}`}>
                <User size={14} />
            </div>
            <span className={`text-[9px] font-bold uppercase tracking-wider ${isLeft ? 'text-blue-300' : 'text-purple-300'}`}>
                {position === 'left' ? 'Left' : 'Right'} Position
            </span>
        </div>
    );
};

// ==================== TREE NODE ====================
const TreeNode = ({ node, level = 0, maxLevel = 50, position = 'root', side, onUserClick }) => {
    const { registerNode, unregisterNode } = useContext(TreeContext);
    const cardRef = useRef(null);

    const hasLeft = node?.left;
    const hasRight = node?.right;
    const showChildren = level < maxLevel;

    const currentSide = level === 0 ? undefined : (side || position);

    useLayoutEffect(() => {
        if (cardRef.current && node?.id) {
            registerNode(node.id, cardRef, position, level, true);
        }
        return () => {
            if (node?.id) unregisterNode(node.id);
        }
    }, [node?.id, position, level, registerNode, unregisterNode]);

    if (level > maxLevel || !node) return null;

    return (
        <div className="flex flex-col items-center relative">
            <div className="relative mb-10">
                <UserCard cardRef={cardRef} user={node} isRoot={level === 0} position={position} side={currentSide} onUserClick={onUserClick} />
            </div>

            {showChildren && (
                <div className="flex gap-6 sm:gap-10 items-start relative">
                    <div className="flex flex-col items-center">
                        {hasLeft ? (
                            <TreeNode node={node.left} level={level + 1} maxLevel={maxLevel} position="left" side={currentSide || 'left'} onUserClick={onUserClick} />
                        ) : (
                            <EmptySlot node_id={node.id} position="left" side={currentSide || 'left'} />
                        )}
                    </div>
                    <div className="flex flex-col items-center">
                        {hasRight ? (
                            <TreeNode node={node.right} level={level + 1} maxLevel={maxLevel} position="right" side={currentSide || 'right'} onUserClick={onUserClick} />
                        ) : (
                            <EmptySlot node_id={node.id} position="right" side={currentSide || 'right'} />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// ==================== GLOBAL CONNECTORS (Clean gray lines) ====================
const GlobalConnectors = ({ treeData, contentRef, containerRef }) => {
    const { nodes } = useContext(TreeContext);
    const [paths, setPaths] = useState([]);
    const [dimensions, setDimensions] = useState({ width: '100%', height: '100%' });
    const [isReady, setIsReady] = useState(false);

    const calculatePaths = useCallback(() => {
        if (!contentRef.current || !treeData || !nodes.current) return false;

        const container = contentRef.current;
        const contentRect = container.getBoundingClientRect();
        if (contentRect.width === 0 || contentRect.height === 0) return false;

        const nodeCount = Object.keys(nodes.current).length;
        if (nodeCount === 0) return false;

        const scrollWidth = container.scrollWidth;
        const scrollHeight = container.scrollHeight;

        setDimensions({ width: scrollWidth, height: scrollHeight });

        const newPaths = [];

        const traverse = (node) => {
            if (!node) return;
            const nodeInfo = nodes.current[node.id];

            let parentX, parentY;
            let parentReady = false;

            if (nodeInfo && nodeInfo.ref.current) {
                const pRect = nodeInfo.ref.current.getBoundingClientRect();
                parentX = pRect.left - contentRect.left + pRect.width / 2;
                parentY = pRect.bottom - contentRect.top;
                parentReady = true;
            }

            const processChild = (childNode, childPos) => {
                const childId = childNode ? childNode.id : `empty-${node.id}-${childPos}`;
                const childInfo = nodes.current[childId];

                if (parentReady && childInfo && childInfo.ref.current) {
                    const cRect = childInfo.ref.current.getBoundingClientRect();
                    const childX = cRect.left - contentRect.left + cRect.width / 2;
                    const childY = cRect.top - contentRect.top;

                    const midY = parentY + (childY - parentY) / 2;

                    newPaths.push({
                        d: `M ${parentX} ${parentY} L ${parentX} ${midY} L ${childX} ${midY} L ${childX} ${childY}`,
                        color: '#d1d5db',
                        endX: childX,
                        endY: childY
                    });
                }

                if (childNode) traverse(childNode);
            };

            processChild(node.left, 'left');
            processChild(node.right, 'right');
        };

        traverse(treeData);
        setPaths(newPaths);
        return true;
    }, [treeData, contentRef, nodes]);

    useEffect(() => {
        if (!treeData) return;

        let attempts = 0;
        const maxAttempts = 50;

        const tryCalculate = () => {
            const result = calculatePaths();
            attempts++;

            if (result) {
                setIsReady(true);
            } else if (attempts < maxAttempts) {
                setTimeout(tryCalculate, 200);
            }
        };

        const initialTimer = setTimeout(tryCalculate, 100);
        return () => clearTimeout(initialTimer);
    }, [treeData, calculatePaths]);

    if (!isReady) return null;

    return (
        <svg
            className="absolute top-0 left-0 pointer-events-none overflow-visible"
            style={{
                zIndex: 5,
                width: dimensions.width,
                height: dimensions.height
            }}
        >
            {paths.map((path, i) => (
                <React.Fragment key={i}>
                    <path
                        d={path.d}
                        fill="none"
                        stroke={path.color}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    <circle cx={path.endX} cy={path.endY} r="3" fill={path.color} />
                </React.Fragment>
            ))}
        </svg>
    );
};

// ==================== BREADCRUMB NAVIGATION ====================
const BreadcrumbNav = ({ trail, onNavigate }) => {
    if (!trail || trail.length <= 1) return null;

    return (
        <div className="flex items-center gap-1 flex-wrap">
            {trail.map((item, idx) => (
                <React.Fragment key={item.id}>
                    {idx > 0 && (
                        <ChevronLeft size={12} className="text-gray-300 shrink-0" />
                    )}
                    <button
                        onClick={() => onNavigate(idx)}
                        className={`text-xs font-semibold px-2 py-1 rounded-lg transition-all ${idx === trail.length - 1
                            ? 'bg-blue-100 text-blue-700 cursor-default'
                            : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'
                            }`}
                        disabled={idx === trail.length - 1}
                    >
                        {item.name}
                    </button>
                </React.Fragment>
            ))}
        </div>
    );
};

// ==================== MAIN COMPONENT ====================
const UserTreeView = ({ user: initialUser }) => {
    const [treeData, setTreeData] = useState(null);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [maxDepth, setMaxDepth] = useState(2);
    const containerRef = useRef(null);
    const contentRef = useRef(null);
    const nodesRef = useRef({});

    // Navigation trail: [{id, name, userId}]
    const [navTrail, setNavTrail] = useState([]);
    // Currently viewed user ID (starting from initialUser)
    const [viewingUserId, setViewingUserId] = useState(null);

    const registerNode = useCallback((id, ref, position, level, expanded) => {
        nodesRef.current[id] = { ref, position, level, expanded };
    }, []);

    const unregisterNode = useCallback((id) => {
        delete nodesRef.current[id];
    }, []);

    const fetchTree = useCallback(async (targetUserId = null) => {
        const idToFetch = targetUserId || initialUser?.id;

        if (!idToFetch) {
            setError("No user selected or user ID missing");
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const token = localStorage.getItem('authToken');

            const url = `${API_BASE_URL}/admin/user_tree.php?user_id=${idToFetch}&max_level=${maxDepth}&token=${token}`;

            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                const text = await response.text();
                throw new Error(`Server returned ${response.status}: ${text.substring(0, 100)}`);
            }

            const data = await response.json();

            if (data.success) {
                nodesRef.current = {};
                const mapped = mapResponseToTree(data.tree);
                setTreeData(mapped);
                setStats(data.stats);

                // Initialize trail if empty
                if (navTrail.length === 0 && mapped) {
                    setNavTrail([{ id: mapped.id, name: mapped.name, userId: mapped.user_id }]);
                }
            } else {
                setError(data.message || "Failed to load tree data");
            }
        } catch (err) {
            console.error('UserTreeView Error:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [initialUser, maxDepth, navTrail.length]);

    const handleUserClick = useCallback((user) => {
        setNavTrail(prev => [...prev, { id: user.id, name: user.name, userId: user.user_id }]);
        setViewingUserId(user.id);
        fetchTree(user.id);
    }, [fetchTree]);

    const handleBreadcrumbNavigate = useCallback((index) => {
        const targetItem = navTrail[index];
        setNavTrail(navTrail.slice(0, index + 1));
        setViewingUserId(targetItem.id);
        fetchTree(targetItem.id);
    }, [navTrail, fetchTree]);

    useEffect(() => {
        fetchTree(viewingUserId);
    }, [fetchTree, viewingUserId]);

    if (loading && !treeData) {
        return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-20 flex flex-col items-center justify-center gap-4">
                <RefreshCw className="w-12 h-12 text-blue-600 animate-spin" />
                <p className="text-blue-600 font-semibold">Building Team Network...</p>
            </div>
        );
    }

    if (error && !treeData) {
        return (
            <div className="bg-red-50 rounded-xl shadow-sm border border-red-200 p-12 flex flex-col items-center justify-center gap-4">
                <AlertTriangle className="w-12 h-12 text-red-500" />
                <div className="text-center">
                    <p className="text-red-600 font-bold text-lg">Unable to load tree</p>
                    <p className="text-red-500 text-sm mt-1">{error}</p>
                </div>
                <button onClick={() => fetchTree(viewingUserId)} className="mt-2 px-6 py-2 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 transition-colors shadow-lg">
                    Retry Fetch
                </button>
            </div>
        );
    }

    return (
        <TreeContext.Provider value={{ nodes: nodesRef, registerNode, unregisterNode }}>
            <div className="space-y-6">
                {/* Header Area with Breadcrumbs */}
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <h2 className="text-xl font-bold text-gray-800">Team Network Layout</h2>
                    </div>
                </div>

                {/* Unified Network Stats Card */}
                {stats && (
                    <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 rounded-2xl p-5 shadow-xl shadow-blue-200/50 text-white flex flex-col justify-between relative overflow-hidden group mb-6">
                        {/* Decorative Icon Background */}
                        <div className="absolute -right-6 -bottom-6 opacity-10 group-hover:opacity-20 transition-opacity">
                            <TrendingUp size={160} />
                        </div>

                        <div className="flex items-start justify-between relative z-10">
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center shadow-inner">
                                    <TrendingUp size={30} className="text-white" />
                                </div>
                                <div>
                                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-100/80">TOTAL PV COUNT</p>
                                    <p className="text-3xl font-black tabular-nums">{Number(stats.total_pv || 0).toLocaleString()} <span className="text-sm font-bold text-blue-200">PV</span></p>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-100/80">TOTAL MEMBERS</p>
                                <p className="text-2xl font-black tabular-nums">{stats.total_downlines || 0}</p>
                            </div>
                        </div>

                        <div className="mt-6 grid grid-cols-2 gap-3 relative z-10">
                            <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/10">
                                <div className="flex justify-between items-start mb-2">
                                    <div>
                                        <p className="text-[10px] font-black uppercase tracking-widest text-blue-100/60 mb-1">LEFT PV</p>
                                        <p className="text-xl font-black">{Number(stats.left_pv || 0).toLocaleString()} <span className="text-[10px] opacity-60">PV</span></p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-[10px] font-black uppercase tracking-widest text-blue-100/60 mb-1">MEMBERS</p>
                                        <p className="text-xl font-black">{stats.left_count || 0}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/10">
                                <div className="flex justify-between items-start mb-2">
                                    <div>
                                        <p className="text-[10px] font-black uppercase tracking-widest text-blue-100/60 mb-1">RIGHT PV</p>
                                        <p className="text-xl font-black">{Number(stats.right_pv || 0).toLocaleString()} <span className="text-[10px] opacity-60">PV</span></p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-[10px] font-black uppercase tracking-widest text-blue-100/60 mb-1">MEMBERS</p>
                                        <p className="text-xl font-black">{stats.right_count || 0}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Tree Controls */}
                <div className="flex items-center justify-between bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
                    <div className="flex gap-4">
                        <div className="flex items-center gap-2 text-[10px] font-bold text-gray-500 bg-gray-50 px-3 py-1.5 rounded-full border">
                            <div className="w-3 h-3 rounded-full bg-gradient-to-br from-amber-400 to-amber-500" /> Leader
                        </div>
                        <div className="flex items-center gap-2 text-[10px] font-bold text-gray-500 bg-gray-50 px-3 py-1.5 rounded-full border">
                            <div className="w-3 h-3 rounded-full bg-gradient-to-br from-blue-500 to-blue-600" /> Left
                        </div>
                        <div className="flex items-center gap-2 text-[10px] font-bold text-gray-500 bg-gray-50 px-3 py-1.5 rounded-full border">
                            <div className="w-3 h-3 rounded-full bg-gradient-to-br from-purple-500 to-purple-600" /> Right
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <select
                            value={maxDepth}
                            onChange={(e) => setMaxDepth(parseInt(e.target.value))}
                            className="bg-white border border-gray-200 text-xs font-bold px-4 py-2 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none shadow-sm cursor-pointer"
                        >
                            <option value={2}>3 Levels (Default)</option>
                            <option value={4}>5 Levels</option>
                            <option value={9}>10 Levels</option>
                            <option value={50}>All Levels</option>
                        </select>
                        <button onClick={() => fetchTree(viewingUserId)} className="p-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 shadow-md transform active:scale-95 transition-all">
                            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                        </button>
                    </div>
                </div>

                {/* Tree Canvas */}
                <div
                    ref={containerRef}
                    className="bg-white rounded-2xl border border-gray-200 shadow-lg relative overflow-auto scroll-smooth"
                    style={{ height: '70vh', minHeight: '600px' }}
                >
                    {/* Sticky Breadcrumbs in Body */}
                    {navTrail && navTrail.length > 1 && (
                        <div className="sticky top-4 left-0 right-0 z-[60] flex justify-center pointer-events-none px-4">
                            <div className="pointer-events-auto bg-white/80 backdrop-blur-md px-4 py-2 rounded-2xl shadow-xl border border-blue-50/50 flex items-center gap-2 max-w-full">
                                <BreadcrumbNav trail={navTrail} onNavigate={handleBreadcrumbNavigate} />
                            </div>
                        </div>
                    )}
                    <div
                        ref={contentRef}
                        className="tree-content min-w-max p-12 sm:p-20 flex justify-center items-start min-h-full relative overflow-visible"
                    >
                        {/* Subtle grid pattern */}
                        <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
                            style={{ backgroundImage: 'radial-gradient(#94a3b8 1px, transparent 1px)', backgroundSize: '32px 32px' }}
                        />

                        {treeData ? (
                            <>
                                <div className="relative z-10">
                                    <TreeNode node={treeData} level={0} maxLevel={maxDepth} onUserClick={handleUserClick} />
                                </div>
                                <GlobalConnectors
                                    key={`connectors-${treeData?.id || 'root'}`}
                                    treeData={treeData}
                                    contentRef={contentRef}
                                    containerRef={containerRef}
                                />
                            </>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-20 text-gray-400 w-full animate-pulse">
                                <Users size={64} className="mb-4 opacity-10" />
                                <p className="font-bold">No network data found for this user</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </TreeContext.Provider>
    );
};

export default UserTreeView;
